import { Children, Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Send, Square } from "lucide-react";
import { toast } from "sonner";
import type { Components } from "react-markdown";
import { Button } from "@/components/ui/button";
import { MarkdownContent } from "@/components/markdown-content";
import { CopyButton } from "@/components/copy-button";
import { markdownToPlainText } from "@/lib/markdown-to-plain-text";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { navPaths } from "@/core/constants";
import { cn } from "@/lib/utils";
import type {
  AskStreamPhase,
  ChatMessage,
  ProcessingStep,
  ReferenceResult,
  SynthesisSession,
} from "../../domain/entities/synthesis-session";
import {
  STEP_LABEL,
  useAskStreamSynthesisSession,
} from "../hooks/use-ask-stream-synthesis-session";
import { segmentCitations } from "./segment-citations";
import { InlineFigureImage, buildImageDocMap } from "./inline-figure-image";

const EMPTY_IMAGE_DOCS: Map<string, string> = new Map();

const PHASE_LABEL: Record<AskStreamPhase, string> = {
  intent_routing: "Đang xác định mục tiêu",
  planning: "Lên kế hoạch",
  narrow_down_kbs: "Chọn tập knowledge liên quan",
  processing: "Đang xử lý",
  review: "Kiểm tra lại kết quả",
  answering: "Trả lời",
};

/**
 * The typing-indicator label for the current stream state (spec §8.3). During
 * `processing` the specific step label wins over the coarse phase label; a
 * stall (`isLongWait`, from the 20s `heartbeat`) appends "(vẫn đang xử lý)"
 * rather than replacing the label. Exported for unit testing.
 */
export function composeIndicatorLabel(
  phase: AskStreamPhase | null,
  step: ProcessingStep | null,
  isLongWait: boolean,
): string | undefined {
  const base = step ? STEP_LABEL[step] : phase ? PHASE_LABEL[phase] : undefined;
  if (!base) return undefined;
  return isLongWait ? `${base} (vẫn đang xử lý)` : base;
}

const composerTextareaClass =
  "min-h-10 max-h-16 w-full flex-1 resize-none overflow-y-auto [field-sizing:fixed] md:text-sm";

const kbdHint = "Enter sends your message. Shift+Enter starts a new line.";
const disclaimer = "AI can make mistakes. Please double-check cited sources";


export const handleSelectRef = (
  references: ReferenceResult[] | undefined,
  id: string,
  openSheet: (payload: { references: ReferenceResult[]; id: string }) => void,
) => {
  const matches = references?.filter((r) => r.id === id) ?? [];
  openSheet({ references: matches, id });
};

export const renderTextWithRefs = (
  text: string,
  references: ReferenceResult[] | undefined,
  onSelectRef: (id: string) => void,
): ReactNode => {
  const ids = references?.map((r) => r.id) ?? [];
  const segments = segmentCitations(text, ids);
  if (segments.length === 1 && segments[0].kind === "text") {
    return segments[0].text;
  }
  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === "text" ? (
          <Fragment key={`t-${i}`}>{seg.text}</Fragment>
        ) : (
          <button
            key={`c-${i}-${seg.id}`}
            type="button"
            onClick={() => onSelectRef(seg.id)}
            aria-label={`Nguồn: ${seg.id}`}
            title={seg.id}
            className="cursor-pointer align-baseline rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2f8f36]/60"
          >
            <span
              aria-hidden
              className="mx-0.5 inline-flex size-4 -translate-y-px select-none items-center justify-center rounded-full bg-[#2f8f36] align-middle text-[10px] font-bold leading-none text-white shadow-sm transition hover:bg-[#247029]"
            >
              !
            </span>
          </button>
        ),
      )}
    </>
  );
};

const transformChildren = (
  children: ReactNode,
  references: ReferenceResult[] | undefined,
  onSelectRef: (id: string) => void,
): ReactNode =>
  Children.map(children, (child) =>
    typeof child === "string"
      ? renderTextWithRefs(child, references, onSelectRef)
      : child,
  );

const buildMarkdownComponents = (
  references: ReferenceResult[] | undefined,
  onSelectRef: (id: string) => void,
  imageDocs: Map<string, string>,
): Components => {
  const wrap =
    <T extends keyof React.JSX.IntrinsicElements>(Tag: T) =>
    ({ children, ...rest }: React.ComponentProps<T> & { node?: unknown }) => {
      const { node: _node, ...domProps } = rest as { node?: unknown } & Record<
        string,
        unknown
      >;
      void _node;
      const Element = Tag as unknown as React.ElementType;
      return (
        <Element {...domProps}>
          {transformChildren(children, references, onSelectRef)}
        </Element>
      );
    };
  return {
    p: wrap("p"),
    li: wrap("li"),
    strong: wrap("strong"),
    em: wrap("em"),
    a: wrap("a"),
    h1: wrap("h1"),
    h2: wrap("h2"),
    h3: wrap("h3"),
    h4: wrap("h4"),
    h5: wrap("h5"),
    h6: wrap("h6"),
    td: wrap("td"),
    th: wrap("th"),
    blockquote: wrap("blockquote"),
    img: ({ src, alt }) => (
      <InlineFigureImage
        src={typeof src === "string" ? src : ""}
        caption={alt ?? ""}
        imageDocs={imageDocs}
      />
    ),
  };
};

type TypingIndicatorProps = {
  label?: string;
};

const TypingIndicator = ({ label }: TypingIndicatorProps) => (
  <div className="flex w-fit flex-col gap-2">
    <div
      className={cn(
        "rounded-2xl bg-transparent px-4 py-2.5 text-base font-light leading-relaxed text-secondary-foreground",
      )}
      aria-label={label ?? "Assistant is typing"}
      role="status"
    >
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="size-1.5 animate-bounce rounded-full bg-current opacity-70 [animation-delay:-0.3s]" />
          <span className="size-1.5 animate-bounce rounded-full bg-current opacity-70 [animation-delay:-0.15s]" />
          <span className="size-1.5 animate-bounce rounded-full bg-current opacity-70" />
        </div>
        {label && <span className="text-xs opacity-80">{label}</span>}
      </div>
    </div>
  </div>
);

type StreamingAssistantMessageProps = {
  text: string;
};

const StreamingAssistantMessage = ({
  text,
}: StreamingAssistantMessageProps) => {
  const components = useMemo<Components>(
    () => ({
      // References aren't final until `done`, so figures can't resolve mid-stream
      // → always the caption placeholder, never a broken relative <img>.
      img: ({ alt }) => (
        <InlineFigureImage src="" caption={alt ?? ""} imageDocs={EMPTY_IMAGE_DOCS} />
      ),
    }),
    [],
  );
  return (
    <div className="flex w-full flex-col gap-2">
      <MarkdownContent
        content={text}
        components={components}
        className="rounded-2xl bg-transparent px-4 py-2.5 text-secondary-foreground"
      />
    </div>
  );
};

type AssistantMessageProps = {
  message: ChatMessage;
  isAsking: boolean;
  isLast: boolean;
  onAskFollowUp: (question: string) => void;
  onSelectRef: (id: string) => void;
};

export const AssistantMessage = ({
  message,
  isAsking,
  isLast,
  onAskFollowUp,
  onSelectRef,
}: AssistantMessageProps) => {
  const imageDocs = useMemo(
    () =>
      buildImageDocMap(message.content, [
        ...(message.references ?? []),
        ...(message.consulted ?? []),
      ]),
    [message.content, message.references, message.consulted],
  );
  const components = useMemo(
    () => buildMarkdownComponents(message.references, onSelectRef, imageDocs),
    [message.references, onSelectRef, imageDocs],
  );
  const followUps = message.followUpQuestions ?? [];
  const showFollowUps = isLast && followUps.length > 0 && !isAsking;

  return (
    <div className="flex w-full flex-col gap-2">
      <MarkdownContent
        content={message.content}
        components={components}
        className="rounded-2xl bg-transparent px-4 py-2.5 text-secondary-foreground"
      />

      <div className="flex">
        <CopyButton
          getText={() =>
            markdownToPlainText(
              message.content.replace(/\[ID:[^\]]+\]/g, ""),
            )
          }
          className="m-0 p-0"
        />
      </div>

      {showFollowUps && (
        <div className="flex flex-col items-start gap-1.5">
          {followUps.map((q, i) => (
            <button
              key={`${i}-${q}`}
              type="button"
              onClick={() => onAskFollowUp(q)}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-left text-sm leading-snug text-foreground transition hover:border-[#2f8f36] hover:bg-[#2f8f36]/5"
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * One reference "card" — the shared rendering unit for both a cited source
 * (inside the reference Sheet) and an uncited "consulted" source. Always
 * shows score + breadcrumb ("why this chunk"); tucks neighbour context
 * (`context.preceding`/`following`) behind a collapsed `<details>` block
 * rendered as PLAIN TEXT — never through the markdown renderer — since that
 * is exactly how `## PREVIOUS CONTEXT:` used to leak into the UI as an `<h2>`.
 */
type ReferenceCardProps = {
  reference: ReferenceResult;
};

export const ReferenceCard = ({ reference }: ReferenceCardProps) => {
  const context = reference.context;
  const hasContext = Boolean(context?.preceding || context?.following);

  return (
    <div className="rounded-md border border-border p-2 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        {reference.breadcrumb && (
          <span>
            <span className="font-semibold">Section:</span> {reference.breadcrumb}
          </span>
        )}
        <span>
          <span className="font-semibold">Độ liên quan:</span>{" "}
          {reference.score.toFixed(2)}
        </span>
      </div>

      <MarkdownContent content={reference.content} />

      {hasContext && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none font-medium">
            Ngữ cảnh xung quanh
          </summary>
          <div className="mt-1 space-y-1">
            {context?.preceding && (
              <div>
                <span className="font-semibold">Đoạn liền trước: </span>
                <span>{context.preceding}</span>
              </div>
            )}
            {context?.following && (
              <div>
                <span className="font-semibold">Đoạn liền sau: </span>
                <span>{context.following}</span>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

type ReferenceSheetBodyProps = {
  references: ReferenceResult[];
};

export const ReferenceSheetBody = ({ references }: ReferenceSheetBodyProps) => {
  const head = references[0];
  const documentLabel = head.docName || head.title || head.documentId;
  const sources = [...references].sort((a, b) => b.score - a.score);

  return (
    <div className="space-y-3 text-sm">
      <div>
        <p className="text-xs uppercase tracking-wide font-bold">Document</p>
        <p className="mt-0.5 break-words">{documentLabel}</p>
      </div>
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide font-bold">
          {sources.length > 1 ? `Sources (${sources.length})` : "Content"}
        </p>
        {sources.map((s, i) => (
          <ReferenceCard key={`src-${i}`} reference={s} />
        ))}
      </div>
    </div>
  );
};

type SynthesisChatThreadProps = {
  session: SynthesisSession | undefined;
  reply: string;
  onReplyChange: (value: string) => void;
  onSend: (override?: string) => void;
  onCancel: () => void;
  isAsking: boolean;
  streamPhase: AskStreamPhase | null;
  streamedText: string;
  step: ProcessingStep | null;
  isLongWait: boolean;
};

const SynthesisChatThread = ({
  session,
  reply,
  onReplyChange,
  onSend,
  onCancel,
  isAsking,
  streamPhase,
  streamedText,
  step,
  isLongWait,
}: SynthesisChatThreadProps) => {
  const kbdHintId = useId();
  const disclaimerId = useId();
  const scrollerRef = useRef<HTMLDivElement>(null);

  const [activeRef, setActiveRef] = useState<{
    references: ReferenceResult[];
    id: string;
  } | null>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [session?.messages, session?.id, isAsking, streamedText, streamPhase]);

  const messages = session?.messages ?? [];
  const lastAssistantIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  })();
  const lastMessage = messages.at(-1);
  const isAwaitingStream = isAsking && lastMessage?.role === "user";
  const showStreamingBubble = isAwaitingStream && streamedText.length > 0;
  const showTypingIndicator = isAwaitingStream && !showStreamingBubble;
  // During `processing` the step label wins over the coarse phase; a 20s stall
  // (isLongWait) augments the label with "(vẫn đang xử lý)" rather than
  // replacing it, so meaningful progress survives the heartbeat.
  const indicatorLabel = composeIndicatorLabel(streamPhase, step, isLongWait);

  const onCitationClick = (
    references: ReferenceResult[] | undefined,
    id: string,
  ) => {
    handleSelectRef(references, id, setActiveRef);
  };

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      <div
        ref={scrollerRef}
        className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4"
      >
        {messages.map((m, i) => (
          <div
            key={m.id}
            className={cn(
              "flex",
              m.role === "user" ? "justify-end" : "justify-start",
            )}
          >
            {m.role === "assistant" ? (
              <AssistantMessage
                message={m}
                isAsking={isAsking}
                isLast={i === lastAssistantIndex}
                onAskFollowUp={(q) => onSend(q)}
                onSelectRef={(id) => onCitationClick(m.references, id)}
              />
            ) : (
              <div className="max-w-[min(100%,40rem)] whitespace-pre-wrap rounded-2xl bg-secondary px-4 py-2.5 text-base font-light leading-relaxed text-secondary-foreground">
                {m.content}
              </div>
            )}
          </div>
        ))}
        {showStreamingBubble && (
          <div className="flex justify-start">
            <StreamingAssistantMessage text={streamedText} />
          </div>
        )}
        {showTypingIndicator && (
          <div className="flex justify-start">
            <TypingIndicator label={indicatorLabel} />
          </div>
        )}
      </div>

      <div
        className={cn(
          "sticky bottom-0 z-10 shrink-0  p-3",
          "bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur-sm",
        )}
      >
        <div className="mx-auto w-full max-w-3xl px-1">
          <p
            id={kbdHintId}
            className="mb-1.5 text-xs leading-snug text-muted-foreground"
          >
            {kbdHint}
          </p>
          <div className="flex items-end gap-2">
            <Textarea
              value={reply}
              onChange={(e) => onReplyChange(e.target.value)}
              placeholder="Message…"
              rows={2}
              className={composerTextareaClass}
              disabled={isAsking}
              aria-describedby={`${kbdHintId} ${disclaimerId}`}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing
                ) {
                  e.preventDefault();
                  if (!isAsking) onSend();
                }
              }}
            />
            {isAsking ? (
              <Button
                type="button"
                size="icon"
                className="size-9 shrink-0"
                onClick={onCancel}
                aria-label="Stop generating"
              >
                <Square className="size-4 fill-current" />
              </Button>
            ) : (
              <Button
                type="button"
                size="icon"
                className="size-9 shrink-0"
                disabled={!reply.trim()}
                onClick={() => onSend()}
                aria-label="Send message"
              >
                <Send className="size-4" />
              </Button>
            )}
          </div>
          <p
            id={disclaimerId}
            className="mt-1.5 text-xs leading-snug text-muted-foreground"
          >
            {disclaimer}
          </p>
        </div>
      </div>

      <Sheet
        open={activeRef !== null}
        onOpenChange={(open) => {
          if (!open) setActiveRef(null);
        }}
      >
        <SheetContent
          side="right"
          className="flex flex-col gap-0 p-0 data-[side=right]:w-[25vw] data-[side=right]:sm:max-w-[25vw]"
        >
          <SheetHeader className="border-b">
            <SheetTitle>Reference</SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {activeRef && activeRef.references.length > 0 ? (
              <ReferenceSheetBody references={activeRef.references} />
            ) : (
              <p className="text-sm text-muted-foreground">
                Reference details are not available for this citation.
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

type SynthesisChatPanelProps = {
  sessionId: string;
  session: SynthesisSession | undefined;
  sessionLoading: boolean;
  sessionError: Error | null;
};

export const SynthesisChatPanel = ({
  sessionId,
  session,
  sessionLoading,
  sessionError,
}: SynthesisChatPanelProps) => {
  const location = useLocation();
  const routerNavigate = useNavigate();
  const autoAskFiredRef = useRef(false);

  const [pendingFirstQuestion] = useState<string | null>(() => {
    const state = location.state as { autoAsk?: { content?: string } } | null;
    return state?.autoAsk?.content?.trim() ?? null;
  });

  const [autoAskStatus, setAutoAskStatus] = useState<
    "idle" | "pending" | "done"
  >(() => (pendingFirstQuestion ? "pending" : "idle"));

  const [localSession, setLocalSession] = useState<
    SynthesisSession | undefined
  >(() => {
    if (pendingFirstQuestion) {
      return {
        id: sessionId,
        messages: [
          {
            id: `optimistic-first-${Date.now()}`,
            role: "user",
            content: pendingFirstQuestion,
          },
        ],
      };
    }
    return session;
  });
  const [reply, setReply] = useState("");
  const {
    start: startAsk,
    cancel: cancelAsk,
    isStreaming: isAsking,
    phase: streamPhase,
    streamedText,
    step,
    isLongWait,
  } = useAskStreamSynthesisSession();

  useEffect(() => {
    if (!session) return;
    if (!localSession) {
      setLocalSession(session);
      return;
    }
    if (session.messages.length > localSession.messages.length) {
      setLocalSession(session);
    }
  }, [session, localSession]);

  useEffect(() => {
    if (autoAskStatus === "pending" && autoAskFiredRef.current && !isAsking) {
      setAutoAskStatus("done");
    }
  }, [autoAskStatus, isAsking]);

  useEffect(() => {
    if (autoAskFiredRef.current) return;
    if (autoAskStatus !== "pending") return;
    if (!pendingFirstQuestion) return;
    if (!session) return;
    autoAskFiredRef.current = true;

    const mergeBase: SynthesisSession = {
      ...session,
      messages: [
        ...session.messages,
        {
          id: `optimistic-first-${Date.now()}`,
          role: "user",
          content: pendingFirstQuestion,
        },
      ],
    };
    setLocalSession(mergeBase);

    startAsk({
      sessionId,
      content: pendingFirstQuestion,
      sessionForMerge: mergeBase,
      onDone: (updated) => {
        setLocalSession(updated);
        setAutoAskStatus("done");
      },
      onError: (e) => {
        setAutoAskStatus("done");
        toast.error(e.message);
      },
    });
    routerNavigate(location.pathname, { replace: true, state: null });
  }, [
    session,
    autoAskStatus,
    pendingFirstQuestion,
    sessionId,
    startAsk,
    routerNavigate,
    location.pathname,
  ]);

  const effectiveIsAsking = isAsking || autoAskStatus === "pending";

  const handleSend = (override?: string) => {
    const t = (override ?? reply).trim();
    if (!t || !localSession || effectiveIsAsking) return;
    if (override === undefined) setReply("");
    const snapshot = localSession;
    const withUser: SynthesisSession = {
      ...snapshot,
      messages: [
        ...snapshot.messages,
        {
          id: `optimistic-${Date.now()}`,
          role: "user",
          content: t,
        },
      ],
    };
    setLocalSession(withUser);
    startAsk({
      sessionId,
      content: t,
      sessionForMerge: withUser,
      onDone: (updated) => {
        setLocalSession(updated);
      },
      onError: (e) => {
        setLocalSession(snapshot);
        toast.error(e.message);
      },
    });
  };

  const handleCancel = () => {
    cancelAsk();
    if (autoAskStatus === "pending") setAutoAskStatus("done");
  };

  if (sessionLoading && !localSession) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Loading conversation…
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
        <p className="text-sm text-destructive">
          {sessionError instanceof Error
            ? sessionError.message
            : "Could not load conversation."}
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to={navPaths.infoSynthesis}>Start over</Link>
        </Button>
      </div>
    );
  }

  return (
    <SynthesisChatThread
      session={localSession}
      reply={reply}
      onReplyChange={setReply}
      onSend={handleSend}
      onCancel={handleCancel}
      isAsking={effectiveIsAsking}
      streamPhase={streamPhase}
      streamedText={streamedText}
      step={step}
      isLongWait={isLongWait}
    />
  );
};
